/**
 * The one rule for turning a person's record into the name and initials a surface shows.
 *
 * Pure and ctx-free on purpose: the Convex backend reads a `users` row, the browser reads a
 * query result or an AuthKit session, and both hand the same three fields to the same
 * function. Thin per-runtime wrappers do the database read; the rule lives only here.
 */
/**
 * The name to show for a person: their given name, else their email address.
 *
 * An address identifies a person; "Unknown User" does not, so there is no placeholder
 * fallback — a record with neither a name nor an email yields `""` and the one caller that
 * needs a label decides what an empty identity looks like on its surface. That is
 * deliberately awkward: a real user row always has an email, so `""` means something is
 * wrong upstream rather than "this person has no name".
 */
export function formatUserDisplayName(user) {
    const name = [user?.firstName, user?.lastName]
        .map((part) => (typeof part === "string" ? part.trim() : ""))
        .filter((part) => part.length > 0)
        .join(" ");
    if (name)
        return name;
    return typeof user?.email === "string" ? user.email.trim() : "";
}
/**
 * Initials for an avatar: the first letter of the first word and of the last word.
 *
 * One rule for every avatar in the product, people and projects alike — a person and a
 * project both named "Mary Jane Watson" read `MW`. A single word gives its first two
 * characters (`Jane` → `JA`); nothing to work with gives `?`.
 */
export function getInitials(name) {
    const trimmed = typeof name === "string" ? name.trim() : "";
    if (!trimmed)
        return "?";
    const words = trimmed.split(/\s+/);
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}
/**
 * What a surface calls a user id that resolves to no record at all — a deleted row, an id a
 * list could not load.
 *
 * This is **not** a name fallback, and it is the reason there is no placeholder in
 * `formatUserDisplayName`: a person with no first or last name is named by their email, while
 * a record that is not there cannot be named at all. Keeping the second case to one string
 * here is what stops it leaking back into the first.
 */
export const UNRESOLVED_USER_LABEL = "Unknown user";
/** Display name and initials together — the pair almost every avatar-and-label surface wants. */
export function formatUserIdentity(user) {
    const displayName = formatUserDisplayName(user);
    return { displayName, initials: getInitials(displayName) };
}
